import { Link } from "react-router-dom";
import {
  Badge, Dropdown, Option, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Text,
  makeStyles, tokens,
} from "@fluentui/react-components";
import type { Priority, Ticket, TicketStatus } from "../types/models";
import { useChangeStatus, useUpdateTicket } from "../api/hooks";

export const CLOSED_STATUSES: TicketStatus[] = ["Resolved", "Closed"];
export const isClosed = (s: TicketStatus) => CLOSED_STATUSES.includes(s);

const PRIORITY_COLOR: Record<Priority, "subtle" | "brand" | "warning" | "danger"> = {
  Low: "subtle",
  Medium: "brand",
  High: "warning",
  Critical: "danger",
};

const STATUS_COLOR: Record<TicketStatus, "subtle" | "brand" | "warning" | "danger" | "informative" | "success"> = {
  New: "subtle",
  Open: "informative",
  Assigned: "brand",
  InProgress: "warning",
  WaitingForUser: "subtle",
  Escalated: "danger",
  Resolved: "success",
  Closed: "success",
  Reopened: "warning",
};

// Human-readable labels — avoids vertical text from camelCase blobs like "WaitingForUser"
const STATUS_LABEL: Record<TicketStatus, string> = {
  New: "New",
  Open: "Open",
  Assigned: "Assigned",
  InProgress: "In Progress",
  WaitingForUser: "Waiting",
  Escalated: "Escalated",
  Resolved: "Resolved",
  Closed: "Closed",
  Reopened: "Reopened",
};

const PRIORITY_BAR: Record<Priority, string> = {
  Critical: "#D13438",
  High: "#F7630C",
  Medium: "#0078D4",
  Low: "#8A8886",
};

const PRIORITIES: Priority[] = ["Low", "Medium", "High", "Critical"];

const useStyles = makeStyles({
  table: {
    tableLayout: "auto",
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  headerRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  headerCell: {
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  row: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  selectedRow: {
    backgroundColor: "rgba(98,100,167,0.07)",
    ":hover": {
      backgroundColor: "rgba(98,100,167,0.11)",
    },
  },
  cell: {
    paddingTop: "10px",
    paddingBottom: "10px",
    verticalAlign: "top",
    whiteSpace: "nowrap",
  },
  idLink: {
    fontFamily: "'Cascadia Code', Consolas, monospace",
    fontWeight: 700,
    fontSize: "13px",
    textDecoration: "none",
    color: tokens.colorBrandForeground1,
    ":hover": {
      textDecoration: "underline",
    },
  },
  titleLink: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    color: tokens.colorNeutralForeground1,
    textDecoration: "none",
    display: "block",
    ":hover": {
      color: tokens.colorBrandForeground1,
      textDecoration: "underline",
    },
  },
  titleButton: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    color: tokens.colorNeutralForeground1,
    display: "block",
    width: "100%",
    ":hover": {
      color: tokens.colorBrandForeground1,
      textDecoration: "underline",
    },
  },
  subText: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  metaCell: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
  },
  unassigned: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground4,
    fontStyle: "italic",
  },
  dateCell: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  emptyWrap: {
    padding: "48px 0",
    textAlign: "center",
  },
});

const ALLOWED_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  New: ["Open", "Assigned", "InProgress", "Escalated", "Closed"],
  Open: ["Assigned", "InProgress", "WaitingForUser", "Escalated", "Resolved", "Closed"],
  Assigned: ["InProgress", "WaitingForUser", "Escalated", "Resolved", "Closed"],
  InProgress: ["WaitingForUser", "Escalated", "Resolved", "Closed"],
  WaitingForUser: ["InProgress", "Resolved", "Closed"],
  Escalated: ["InProgress", "Resolved", "Closed"],
  Resolved: ["Closed", "Reopened"],
  Closed: ["Reopened"],
  Reopened: ["Open", "Assigned", "InProgress"],
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return "Today " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return "Yesterday " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  return (
    d.toLocaleDateString(undefined, { month: "short", day: "2-digit" }) +
    " " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
  );
}

function SlaCell({ ticket }: { ticket: Ticket }) {
  if (isClosed(ticket.status) || ticket.status === "Resolved") {
    return <span style={{ color: "#8A8886", fontSize: 12 }}>—</span>;
  }
  if (!ticket.slaDueAt) return <span style={{ color: "#8A8886", fontSize: 12 }}>—</span>;
  const due = new Date(ticket.slaDueAt);
  const now = new Date();
  const diffMs = due.getTime() - now.getTime();
  if (diffMs < 0) {
    return (
      <Badge color="danger" appearance="filled" size="small">
        Breached
      </Badge>
    );
  }
  const diffH = Math.floor(diffMs / 3600000);
  const diffM = Math.floor((diffMs % 3600000) / 60000);
  const label = diffH > 0 ? `${diffH}h ${diffM}m` : `${diffM}m`;
  if (diffH < 2) return <Badge color="danger" appearance="tint" size="small">{label}</Badge>;
  if (diffH < 8) return <Badge color="warning" appearance="tint" size="small">{label}</Badge>;
  return <Badge color="success" appearance="tint" size="small">{label}</Badge>;
}

function EditablePriorityCell({ ticket }: { ticket: Ticket }) {
  const m = useUpdateTicket(ticket.id);
  return (
    <Dropdown
      size="small"
      value={ticket.priority}
      selectedOptions={[ticket.priority]}
      disabled={m.isPending}
      title={m.isError ? (m.error as Error).message : undefined}
      onOptionSelect={(_, d) => {
        const next = d.optionValue as Priority | undefined;
        if (next && next !== ticket.priority) m.mutate({ priority: next });
      }}
    >
      {PRIORITIES.map((p) => (
        <Option key={p} value={p} text={p}>{p}</Option>
      ))}
    </Dropdown>
  );
}

function EditableStatusCell({ ticket }: { ticket: Ticket }) {
  const m = useChangeStatus(ticket.id);
  const allowed = ALLOWED_TRANSITIONS[ticket.status] ?? [];
  return (
    <Dropdown
      size="small"
      value={STATUS_LABEL[ticket.status]}
      selectedOptions={[ticket.status]}
      style={{ minWidth: 130 }}
      disabled={m.isPending || allowed.length === 0}
      title={m.isError ? (m.error as Error).message : undefined}
      onOptionSelect={(_, d) => {
        const next = d.optionValue as TicketStatus | undefined;
        if (next && next !== ticket.status) m.mutate({ status: next });
      }}
    >
      <Option value={ticket.status} text={STATUS_LABEL[ticket.status]} disabled>
        {STATUS_LABEL[ticket.status]}
      </Option>
      {allowed.map((s) => (
        <Option key={s} value={s} text={STATUS_LABEL[s]}>{STATUS_LABEL[s]}</Option>
      ))}
    </Dropdown>
  );
}

export function TicketTable(props: {
  tickets: Ticket[];
  showRequester?: boolean;
  editable?: boolean;
  onSelect?: (ticket: Ticket) => void;
  selectedId?: string;
}) {
  const s = useStyles();

  if (props.tickets.length === 0) {
    return (
      <div className={s.emptyWrap}>
        <Text style={{ color: "#8A8886", fontStyle: "italic" }}>No tickets to show.</Text>
      </div>
    );
  }

  const editable = !!props.editable;
  const { onSelect, selectedId } = props;

  return (
    <Table aria-label="Tickets" className={s.table}>
      <TableHeader>
        <TableRow className={s.headerRow}>
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: 90 }}>Ticket</TableHeaderCell>
          <TableHeaderCell className={s.headerCell} style={{ width: "40%" }}>Title</TableHeaderCell>
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: editable ? 160 : 110 }}>Status</TableHeaderCell>
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: editable ? 130 : 90 }}>Priority</TableHeaderCell>
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: 90 }}>SLA</TableHeaderCell>
          {props.showRequester && (
            <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: 130 }}>Requester</TableHeaderCell>
          )}
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: 130 }}>Assignee</TableHeaderCell>
          <TableHeaderCell className={s.headerCell} style={{ whiteSpace: "nowrap", width: 130 }}>Updated</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.tickets.map((t) => {
          const selected = selectedId === t.id;
          return (
            <TableRow
              key={t.id}
              className={selected ? s.selectedRow : s.row}
              style={{ boxShadow: `inset 4px 0 0 ${PRIORITY_BAR[t.priority]}` }}
            >
              <TableCell className={s.cell}>
                <Link to={`/tickets/${t.id}`} className={s.idLink}>
                  IT-{t.ticketNumber}
                </Link>
              </TableCell>

              <TableCell className={s.cell} style={{ whiteSpace: "normal" }}>
                {onSelect ? (
                  <button
                    className={s.titleButton}
                    onClick={() => onSelect(t)}
                    style={{ fontWeight: selected ? 700 : 600 }}
                  >
                    {t.title}
                  </button>
                ) : (
                  <Link to={`/tickets/${t.id}`} className={s.titleLink}>
                    {t.title}
                  </Link>
                )}
                <span className={s.subText}>
                  {[t.category?.name, t.queue?.name].filter(Boolean).join(" · ") || "No category"}
                </span>
              </TableCell>

              <TableCell className={s.cell} style={{ whiteSpace: "nowrap" }}>
                {editable ? (
                  <EditableStatusCell ticket={t} />
                ) : (
                  <Badge color={STATUS_COLOR[t.status]} appearance="tint" style={{ whiteSpace: "nowrap" }}>
                    {STATUS_LABEL[t.status]}
                  </Badge>
                )}
              </TableCell>

              <TableCell className={s.cell}>
                {editable ? (
                  <EditablePriorityCell ticket={t} />
                ) : (
                  <Badge color={PRIORITY_COLOR[t.priority]} appearance="tint">
                    {t.priority}
                  </Badge>
                )}
              </TableCell>

              <TableCell className={s.cell}>
                <SlaCell ticket={t} />
              </TableCell>

              {props.showRequester && (
                <TableCell className={`${s.cell} ${s.metaCell}`}>
                  {t.requester?.displayName ?? "—"}
                </TableCell>
              )}

              <TableCell className={s.cell}>
                {t.assignee ? (
                  <span className={s.metaCell}>{t.assignee.displayName}</span>
                ) : (
                  <span className={s.unassigned}>Unassigned</span>
                )}
              </TableCell>

              <TableCell className={`${s.cell} ${s.dateCell}`}>
                {fmtDate(t.updatedAt)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
